import React, { ReactElement } from 'react'
import { Button, Columns, Element, Icon } from 'react-bulma-components'
import { Mission } from '../../api/missions'
import { useUpdateMutation } from '../../queries/work-periods'
import ButtonWithTooltip from '../button-with-tooltip/button-with-tooltip'
import { AddResourceIcon, MissionCancelledIcon, MissionValidatedIcon } from '../icons'
import { useModal } from '../modal'
import Protected, { isAuthorizedFor } from '../protected/protected'
import { MissionIsCancelledMessage, MissionIsValidatedMessage } from './mission-messages'
import MissionCancelConfirmation from './modals/cancel-confirmation'
import MissionValidateConfirmation from './modals/validate-mission-confirmation'
import AddWorkPeriodModal from './modals/add-work-period-modal'
import WorkerLabel from '../texts/worker-label'
import ResourceActions from '../resources/resource-actions'

interface MissionActionsProps {
  mission: Mission
}

const MissionActions: React.FC<MissionActionsProps> = ({ mission }) => {
  const actions = useActions(mission)

  return <ResourceActions actions={actions} />
}

export default MissionActions

interface Action {
  //TODO : refactor (same code in work-periods-actions.tsx)
  title: string
  description?: string | ReactElement
  content: React.ReactNode
}

const useActions = (mission: Mission): Action[] => {
  const modal = useModal()
  const addWorkPeriodModal = useModal()
  //Validate mission confirmation state
  const [currentMissionToValidate, setCurrentMissionToValidate] = React.useState<Mission>()

  //Mutations
  const updateWorkPeriodMutation = useUpdateMutation()
  const firstCancellableWorkPeriod = mission.workPeriods.find(
    workPeriod => workPeriod.__actions.canBeCancelled,
  )
  const missionIsCancellable = firstCancellableWorkPeriod !== undefined

  //TODO: duplicate of backend check (found  a solution to avoid duplicate code)
  const missionCanBeValidated = mission.workPeriods.every(w => w.__actions.canBeValidated)

  // TODO: refactor using workPeriod.__action.canBeCancelled
  return React.useMemo(
    () =>
      [
        isAuthorizedFor(['employerMember'], { resource: 'missions', name: 'validate' }) && {
          title: "Valider l'entièreté de la Mission",
          description: (
            <>
              Vous pouvez valider l'entièreté d'une mission si l'ensemble de ses Journées de Travail
              ne sont pas vides.
              <br />
              Chaque journée doit avoir au moins l'une des informations suivantes :
              <ul>
                <li>- Soit un pointage de début et de fin</li>
                <li>- Soit un événement</li>
                <li>- Soit être en Abandon de Poste</li>
              </ul>
            </>
          ),
          content: (
            <Protected roles={['employerMember']}>
              <Columns>
                {mission.isValidated && (
                  <Columns.Column>
                    <MissionIsValidatedMessage mission={mission} />
                  </Columns.Column>
                )}
                {!mission.isValidated && (
                  <Columns.Column narrow>
                    <ButtonWithTooltip
                      type="button"
                      color={'primary'}
                      onClick={() => setCurrentMissionToValidate(mission)}
                      disabled={!Boolean(missionCanBeValidated)}
                      tooltip={{
                        text: 'Cette Mission ne peut être validée',
                        show: !missionCanBeValidated,
                      }}
                      data-test="validate-mission"
                    >
                      <Icon>
                        <MissionValidatedIcon />
                      </Icon>
                      <span>Valider</span>
                    </ButtonWithTooltip>
                    {missionCanBeValidated && currentMissionToValidate && (
                      <MissionValidateConfirmation
                        show={modal.isDisplayed}
                        hide={() => modal.setIsDisplayed(false)}
                        mission={mission}
                      />
                    )}
                  </Columns.Column>
                )}
              </Columns>
            </Protected>
          ),
        },
        isAuthorizedFor(['employerMember', 'interimAgencyMember'], {
          resource: 'missions',
          name: 'cancel',
        }) && {
          title: 'Interrompre le reste de la Mission',
          description: (
            <>
              L'Interruption d'une Mission a pour effet de
              <ul>
                <li>- Préserver les Journées de Travail passées et en cours</li>
                <li>- Supprimer toutes les Journées de Travail à venir</li>
                <li>- Notifier l'Employeur de l'Interruption</li>
                <li>
                  - Si le <WorkerLabel /> est employé par une Entreprise de Travail Temporaire vous
                  pourrez demander un remplaçant{' '}
                </li>
                <li>
                  - Le <WorkerLabel /> sera disponible à l'issue de sa dernière Journée de Travail
                  préservée
                </li>
              </ul>
            </>
          ),
          content: (
            <Protected roles={['interimAgencyMember', 'employerMember']}>
              <Columns>
                {mission.isCancelled && (
                  <Columns.Column>
                    <MissionIsCancelledMessage mission={mission} />
                  </Columns.Column>
                )}
                {!mission.isCancelled && (
                  <Columns.Column narrow>
                    <ButtonWithTooltip
                      type="button"
                      color={'danger'}
                      onClick={() => firstCancellableWorkPeriod && modal.setIsDisplayed(true)}
                      disabled={!Boolean(firstCancellableWorkPeriod)}
                      tooltip={{
                        text: "Cette Mission n'est pas interruptible",
                        show: !missionIsCancellable,
                      }}
                      data-test="cancel-mission"
                    >
                      <Icon>
                        <MissionCancelledIcon />
                      </Icon>
                      <span>Interrompre</span>
                    </ButtonWithTooltip>
                    {missionIsCancellable && (
                      <MissionCancelConfirmation
                        show={modal.isDisplayed}
                        hide={() => modal.setIsDisplayed(false)}
                        mission={mission}
                        workPeriod={firstCancellableWorkPeriod}
                      />
                    )}
                  </Columns.Column>
                )}
              </Columns>
            </Protected>
          ),
        },
        isAuthorizedFor(['employerMember'], { resource: 'missions', name: 'createWorkPeriod' }) &&
          !mission.scheduleTemplate &&
          mission.__actions?.canBeUpdated && {
            title: 'Ajouter une journée de travail',
            description:
              'Créez une nouvelle journée de travail dans la plage de dates de la mission. Les dates ne doivent pas chevaucher une journée existante.',
            content: (
              <>
                <Button
                  color="primary"
                  onClick={() => addWorkPeriodModal.setIsDisplayed(true)}
                  data-test="add-work-period-button"
                >
                  <Icon>
                    <AddResourceIcon />
                  </Icon>
                  <Element renderAs="span">Ajouter une journée</Element>
                </Button>
                <AddWorkPeriodModal
                  show={addWorkPeriodModal.isDisplayed}
                  hide={() => addWorkPeriodModal.setIsDisplayed(false)}
                  mission={mission}
                />
              </>
            ),
          },
      ].filter(v => v) as Action[],
    [mission, firstCancellableWorkPeriod, updateWorkPeriodMutation, modal, addWorkPeriodModal],
  )
}
